/* =====================================================================
   HL LONDON - CONSENT GATE (q403 Phase 3, restructured 14 Sept 2026)

   THE HIERARCHY, AND WHY IT IS DEFENSIBLE
   ---------------------------------------
   Two primary buttons: ACCEPT (filled espresso, the obvious press) and
   CUSTOMISE (outline, same size, quieter). BELOW them, a plain
   underlined text link: REJECT.

   Rejecting is ONE CLICK from the first screen, exactly like accepting.
   It is never moved behind Customise and never costs an extra step. The
   link is set at the body type scale in espresso on ivory (13.6:1) with
   a 44px tap target, so it is genuinely readable rather than nominally
   present. If you are tempted to shrink it, grey it, or push it behind
   the preferences step: that is the change that makes this indefensible
   under UK GDPR / PECR. Do not.

   IT DRIVES ANALYZELY, IT DOES NOT REPLACE IT.
   Measured on the live theme, 14 Sept 2026:
     - Analyzely's app embed renders ONLY two buttons, Accept and
       Decline. It has NO granular preferences panel of any kind, so
       the Customise panel below is ours by necessity, not by choice.
     - Clicking either of its buttons hides its wrapper permanently
       (display:none, and it stays hidden across reloads).
     - Shopify.customerPrivacy.setTrackingConsent() writes per-category
       consent that persists across reloads, and a write made AFTER
       Analyzely's own click survives intact.
   So: every path clicks one of Analyzely's buttons to retire its bar,
   then writes the precise consent through Shopify's API, which is the
   canonical store the pixels actually read.

   CRAWLERS. The page HTML is complete before this runs - the gate is an
   extra fixed layer, not a replacement for the document - so a crawler
   that reads HTML is unaffected. For crawlers that RENDER, the gate is
   skipped outright on a known bot user agent, so Googlebot sees pages.

   HONEST LIMIT: this is JavaScript. With JS disabled there is no gate,
   and no Analyzely banner either. No theme-side gate can be otherwise.
   ===================================================================== */
(function () {
  'use strict';

  var STORE_KEY = 'hl_consent_choice_v1';
  var CATS_KEY  = 'hl_consent_cats_v1';
  var BOT = /bot|crawl|spider|slurp|bingpreview|headlesschrome|lighthouse|gtmetrix|pingdom|ahrefs|semrush|facebookexternalhit|embedly|whatsapp|telegram|applebot|duckduckbot|baiduspider|yandex|petalbot|google-inspectiontool/i;

  if (BOT.test(navigator.userAgent || '')) return;

  /* ---- has the visitor already decided? ------------------------------ */
  function storedChoice() {
    try {
      var v = window.localStorage.getItem(STORE_KEY);
      if (v === 'accept' || v === 'reject' || v === 'custom') return v;
    } catch (e) { /* private mode */ }
    var m = document.cookie.match(new RegExp('(?:^|; )' + STORE_KEY + '=(accept|reject|custom)'));
    return m ? m[1] : null;
  }

  function shopifyDecided() {
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      var c = cp && cp.currentVisitorConsent && cp.currentVisitorConsent();
      if (!c) return false;
      /* Shopify returns '' for "not asked" and yes/no once answered. */
      return (c.analytics === 'yes' || c.analytics === 'no' ||
              c.marketing === 'yes' || c.marketing === 'no');
    } catch (e) { return false; }
  }

  function remember(choice, cats) {
    var a = new Date();
    a.setFullYear(a.getFullYear() + 1);
    try {
      window.localStorage.setItem(STORE_KEY, choice);
      if (cats) window.localStorage.setItem(CATS_KEY, JSON.stringify(cats));
    } catch (e) {}
    document.cookie = STORE_KEY + '=' + choice + '; path=/; expires=' + a.toUTCString() + '; SameSite=Lax';
  }

  /* ---- write the decision through every channel we have ---------------
     Order matters and was measured: Analyzely's button FIRST (it retires
     its own bar and writes an all-or-nothing baseline), our precise
     per-category write SECOND, because the later write is the one that
     sticks. Reversing these loses a mixed selection. */
  function record(choice, cats) {
    var allOn = choice === 'accept';
    cats = cats || {
      analytics: allOn, marketing: allOn,
      preferences: allOn, sale_of_data: allOn
    };

    /* 1. retire the app's own bar for good */
    var btn = document.getElementById(
      (choice === 'accept')
        ? 'analyzely-consent-banner__accept-button'
        : 'analyzely-consent-banner__decline-button');
    if (btn) { try { btn.click(); } catch (e) {} }

    /* 2. the precise per-category truth, which Shopify's pixels read */
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      if (cp && cp.setTrackingConsent) {
        cp.setTrackingConsent({
          analytics:    !!cats.analytics,
          marketing:    !!cats.marketing,
          preferences:  !!cats.preferences,
          sale_of_data: !!cats.sale_of_data
        }, function () {});
      }
    } catch (e) {}

    remember(choice, cats);
  }

  if (storedChoice() || shopifyDecided()) return;

  /* ---- build it ------------------------------------------------------- */
  var lastFocus = null;

  function build() {
    if (document.getElementById('hl-consent-gate')) return;
    if (storedChoice() || shopifyDecided()) return;

    var gate = document.createElement('div');
    gate.className = 'hl-cgate';
    gate.id = 'hl-consent-gate';
    gate.setAttribute('role', 'dialog');
    gate.setAttribute('aria-modal', 'true');
    gate.setAttribute('aria-labelledby', 'hl-cgate-title');
    gate.setAttribute('aria-describedby', 'hl-cgate-body');

    var policy = window.hlConsentPolicyUrl || '/policies/privacy-policy';

    /* Category rows. "Essential" is locked on: it is what makes the bag
       and the checkout work, and it is not consent-based. */
    var CATS = [
      { key: 'essential',   label: 'Essential',       note: 'Keeps your bag and checkout working. Always on.', locked: true },
      { key: 'analytics',   label: 'Analytics',       note: 'Tells us which pieces people look at, so we buy better.' },
      { key: 'marketing',   label: 'Marketing',       note: 'Lets us show you our pieces elsewhere, and leave out the ones you would never wear.' },
      { key: 'preferences', label: 'Personalisation', note: 'Remembers your size, your country and what you have viewed.' }
    ];

    function rowHtml(c) {
      var id = 'hl-cgate-cat-' + c.key;
      return '<div class="hl-cgate__cat">' +
        '<div class="hl-cgate__cat-text">' +
          '<label class="hl-cgate__cat-label" for="' + id + '">' + c.label + '</label>' +
          '<p class="hl-cgate__cat-note">' + c.note + '</p>' +
        '</div>' +
        '<span class="hl-cgate__switch' + (c.locked ? ' is-locked' : '') + '">' +
          '<input type="checkbox" id="' + id + '" data-hl-cat="' + c.key + '"' +
            (c.locked ? ' checked disabled' : '') + '>' +
          '<span class="hl-cgate__switch-track" aria-hidden="true"></span>' +
        '</span>' +
      '</div>';
    }

    gate.innerHTML =
      '<div class="hl-cgate__panel">' +

        /* ---------- step 1: the choice ---------- */
        '<div class="hl-cgate__step" data-hl-step="choose">' +
          '<p class="hl-cgate__eyebrow">H&amp;L London</p>' +
          '<h2 class="hl-cgate__title" id="hl-cgate-title">Before you browse</h2>' +
          '<p class="hl-cgate__body" id="hl-cgate-body">We use cookies so we can show you the right pieces &mdash; and leave out the ones you would never wear. ' +
            '<a href="' + policy + '" target="_blank" rel="noopener noreferrer">Privacy policy</a></p>' +
          '<div class="hl-cgate__actions">' +
            '<button type="button" class="hl-cgate__btn hl-cgate__btn--primary" data-hl-consent="accept">Accept</button>' +
            '<button type="button" class="hl-cgate__btn hl-cgate__btn--secondary" data-hl-step-to="customise">Customise</button>' +
          '</div>' +
          /* THE REJECT LINK. First screen, one click, body scale,
             espresso on ivory, underlined, 44px tall. */
          '<div class="hl-cgate__reject-row">' +
            '<button type="button" class="hl-cgate__reject" data-hl-consent="reject">Reject</button>' +
          '</div>' +
        '</div>' +

        /* ---------- step 2: granular preferences ---------- */
        '<div class="hl-cgate__step" data-hl-step="customise" hidden>' +
          '<p class="hl-cgate__eyebrow">H&amp;L London</p>' +
          '<h2 class="hl-cgate__title">Choose your cookies</h2>' +
          '<p class="hl-cgate__body">Turn on only what you are happy with. The site works either way.</p>' +
          '<div class="hl-cgate__cats">' + CATS.map(rowHtml).join('') + '</div>' +
          '<div class="hl-cgate__actions">' +
            '<button type="button" class="hl-cgate__btn hl-cgate__btn--primary" data-hl-save="1">Save preferences</button>' +
          '</div>' +
          '<div class="hl-cgate__reject-row">' +
            '<button type="button" class="hl-cgate__reject" data-hl-step-to="choose">Back</button>' +
          '</div>' +
        '</div>' +

      '</div>';

    document.body.appendChild(gate);
    document.documentElement.classList.add('hl-cgate-open');

    lastFocus = document.activeElement;
    var steps = {};
    [].forEach.call(gate.querySelectorAll('[data-hl-step]'), function (s) {
      steps[s.getAttribute('data-hl-step')] = s;
    });

    function current() { return steps.customise.hidden ? steps.choose : steps.customise; }
    function focusables() {
      return [].slice.call(current().querySelectorAll(
        'a[href],button:not([disabled]),input:not([disabled])'));
    }

    /* TAKE FOCUS, AND KEEP TAKING IT.
       Measured on the live theme 14 Sept 2026: Attentive's Spin-to-Win
       iframe had already made itself document.activeElement by the time
       the gate was up. The CSS hides that iframe while the gate is open,
       but a hidden element can still HOLD focus, which would leave a
       keyboard user tabbing around inside an invisible cross-origin
       frame with no way back. A capture-phase focus listener cannot
       catch that, because focus moving into a cross-origin iframe fires
       no event we can see. So: blow focus away on entry, then re-assert
       it for the first few seconds, which is the window in which the
       third-party tags finish loading and grab it. */
    function claimFocus() {
      var acc = gate.querySelector('[data-hl-consent="accept"]');
      try {
        var a = document.activeElement;
        if (a && a !== acc && !gate.contains(a) && a.blur) a.blur();
      } catch (e) {}
      /* Step-aware, so the guard does not drag a visitor off the
         preferences panel and back to the first screen. Focus lands on
         the first BUTTON, not focusables()[0] -- that would be the
         privacy-policy link, which sits earlier in the DOM and is not
         the action anyone arrived to take. */
      if (!gate.contains(document.activeElement)) {
        var f = current().querySelectorAll('button:not([disabled])');
        (f.length ? f[0] : acc).focus();
      }
    }
    claimFocus();
    var focusGuard = setInterval(claimFocus, 400);
    setTimeout(function () { clearInterval(focusGuard); }, 6000);

    /* Announce it: role=dialog alone is not reliably spoken on arrival. */
    var live = document.createElement('div');
    live.setAttribute('aria-live', 'assertive');
    live.setAttribute('role', 'status');
    live.style.cssText = 'position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap';
    live.textContent = 'Cookie choice required. Accept, customise, or reject cookies to continue.';
    gate.appendChild(live);

    function goTo(name) {
      steps.choose.hidden = (name !== 'choose');
      steps.customise.hidden = (name !== 'customise');
      /* First control of the step: the toggles on the preferences panel,
         the Accept button on the first screen. Not focusables()[0],
         which would be the privacy link. */
      var f = current().querySelectorAll('input:not([disabled]),button:not([disabled])');
      if (f.length) f[0].focus();
      live.textContent = (name === 'customise')
        ? 'Cookie preferences. Choose categories, then save.'
        : 'Cookie choice required. Accept, customise, or reject cookies to continue.';
    }

    /* FOCUS TRAP. Escape does NOT dismiss - a dialog you can dismiss
       without choosing is not a gate. On the preferences step Escape
       steps back rather than doing nothing, which is what a keyboard
       user expects of a sub-panel. */
    function trap(e) {
      if (e.key === 'Escape') {
        e.preventDefault(); e.stopPropagation();
        if (!steps.customise.hidden) goTo('choose');
        return;
      }
      if (e.key !== 'Tab') return;
      var f = focusables();
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }

    /* Anything outside the dialog that steals focus gets it taken back. */
    function refocus(e) {
      if (!gate.contains(e.target)) {
        e.stopPropagation();
        var f = focusables();
        if (f.length) f[0].focus();
      }
    }

    function close(choice, cats) {
      record(choice, cats);
      clearInterval(focusGuard);
      document.documentElement.classList.remove('hl-cgate-open');
      gate.removeEventListener('keydown', trap, true);
      document.removeEventListener('focus', refocus, true);
      if (gate.parentNode) gate.parentNode.removeChild(gate);
      try { if (lastFocus && lastFocus.focus) lastFocus.focus(); } catch (e) {}
    }

    gate.addEventListener('click', function (e) {
      var t = e.target && e.target.closest
        ? e.target.closest('[data-hl-consent],[data-hl-step-to],[data-hl-save]') : null;
      if (!t) return;
      if (t.hasAttribute('data-hl-step-to')) { goTo(t.getAttribute('data-hl-step-to')); return; }
      if (t.hasAttribute('data-hl-save')) {
        var cats = { analytics: false, marketing: false, preferences: false, sale_of_data: false };
        [].forEach.call(gate.querySelectorAll('[data-hl-cat]'), function (i) {
          var k = i.getAttribute('data-hl-cat');
          if (k !== 'essential') cats[k] = i.checked;
        });
        cats.sale_of_data = cats.marketing;
        close('custom', cats);
        return;
      }
      close(t.getAttribute('data-hl-consent'));
    });

    gate.addEventListener('keydown', trap, true);
    document.addEventListener('focus', refocus, true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', build);
  } else {
    build();
  }
})();
